import {AxiosResponse} from 'axios';
import {useQuery} from '@tanstack/react-query';
import {apiGet} from '../../axiosConfig';
import {IApiDataResponse} from '../../dto/API.dto';
import {IWalkupRequest} from '../../dto/IWalkupRequest.dto';
import {IWalkupPlayer} from '../../dto/IWalkupPlayer.dto';
import {IMediaTrack} from '../../dto/MediaTrack.dto';
import {ISongRequest} from '../../dto/SongRequest.dto';
import {
  INewWalkupPlayer,
  IWalkupLibraryTypeProp,
} from '../../pages/music/walkup/stepperComponents/WalkupLineupStepper';
import {IStation} from '../../dto/Station.dto';

export const getWalkupRequestShowApiPath = (
  company_id: number,
  walkup_request_id: number,
) => `/v1/company/${company_id}/walkup/${walkup_request_id}`;

export type IWalkupPlayerType = IWalkupPlayer & {
  file?: File | null;
  track_artist?: string;
  track_name?: string;
  type?: string;
  track?: IMediaTrack | null;
  song_request?: ISongRequest;
  isPlayerDisabled?: boolean;
  order?: number;
};

export type IWalkupRequestResp = IWalkupRequest & {
  players: INewWalkupPlayer[];
  form_student_ids: number[];
  station_id: number;
  stations?: IStation[];
  walkup_type: IWalkupLibraryTypeProp;
};

const getWalkupRequestShow = (
  company_id: number,
  walkup_request_id: number,
) => {
  return apiGet(
    getWalkupRequestShowApiPath(company_id, walkup_request_id),
  ).then((response: AxiosResponse<IApiDataResponse<IWalkupRequestResp>>) => {
    return response.data.data;
  });
};

export const useRqWalkupRequestShow = (
  company_id: number,
  walkup_request_id: number,
) => {
  return useQuery({
    queryKey: [getWalkupRequestShowApiPath(company_id, walkup_request_id)],
    queryFn: () => getWalkupRequestShow(company_id, walkup_request_id),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: false,
    enabled: Boolean(walkup_request_id),
  });
};

// Students forms list based on library id

export type IWalkupStudentForm = {
  id: number;
  company_id: number;
  walkup_library_id: number;
  name: string;
  link_status: boolean;
  link_token: string;
  expired_at: string;
  created_at: string;
};

export const walkupStudentFormsApiPath = (
  companyId: number,
  walkupId: number,
) => `v1/company/${companyId}/walkup/${walkupId}/student-form`;

const walkupStudentForms = (companyId: number, walkupId: number) => {
  return apiGet(walkupStudentFormsApiPath(companyId, walkupId)).then(
    (response: AxiosResponse<IApiDataResponse<IWalkupStudentForm[]>>) => {
      return response.data.data;
    },
  );
};

export const useRqWalkupStudentForms = (
  companyId: number,
  walkupId: number,
) => {
  return useQuery({
    queryKey: [walkupStudentFormsApiPath(companyId, walkupId)],
    queryFn: () => walkupStudentForms(companyId, walkupId),
    refetchOnWindowFocus: false,
    retry: false,
  });
};

// Students forms details based on student form id.

export type IWalkupStudentFormPlayers = IWalkupStudentForm & {
  players: IWalkupStudentFormResponse[];
};

export interface WalkupPlayerDetails {
  id: number;
  walkup_player_id: number;
  lineup_track_id: number | null;
  walkup_track_id: number | null;
  type: 'H' | 'P' | 'A'; // Hitter, Pitcher, Athlete
  order: number;
  created_at: string; // ISO datetime string
  updated_at: string; // ISO datetime string
  track_name?: string | null;
  track_artist?: string | null;
  track?: IMediaTrack | null;
  song_request_summary?: {
    track_name?: string | null;
    track_artist?: string | null;
    media_track_id?: number | null;
  } | null;
}

export type IWalkupStudentFormResponse = {
  walkup_student_form_id: number;
  walkup_player_id: number;
  walkup_player_detail_id: number;
  media_track_id: number | null;
  player: IWalkupPlayer | null;
  track: IMediaTrack | null;
  walkup_player_details?: WalkupPlayerDetails;
};

export const walkupStudentFormResponseApiPath = (
  companyId: number,
  walkupId: number,
  formId: number,
) => `v1/company/${companyId}/walkup/${walkupId}/student-form/${formId}`;

const walkupStudentFormResponse = (
  companyId: number,
  walkupId: number,
  formId: number,
) => {
  return apiGet(
    walkupStudentFormResponseApiPath(companyId, walkupId, formId),
  ).then(
    (response: AxiosResponse<IApiDataResponse<IWalkupStudentFormPlayers>>) => {
      return response.data.data;
    },
  );
};

export const useRqWalkupStudentFormResponse = (
  companyId: number,
  walkupId: number,
  formId: number,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [walkupStudentFormResponseApiPath(companyId, walkupId, formId)],
    queryFn: () => walkupStudentFormResponse(companyId, walkupId, formId),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled,
  });
};

// Players list by student form token
export const getWalkupRequestShowByTokenApiPath = (token: string) =>
  `/v1/walkup-student-form/${token}`;

const getWalkupRequestShowByToken = (token: string) => {
  return apiGet(getWalkupRequestShowByTokenApiPath(token)).then(
    (response: AxiosResponse<IApiDataResponse<IWalkupRequestResp>>) => {
      return response.data.data;
    },
  );
};

export const useRqWalkupRequestShowByToken = (
  token: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [getWalkupRequestShowByTokenApiPath(token)],
    queryFn: () => getWalkupRequestShowByToken(token),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: false,
    enabled: enabled,
  });
};

// Players list by student form token

export interface IWalkupLibraryPlayer {
  id: number;
  player_no: string;
  first_name: string;
  last_name: string;
  walkup_library_id: number;
}

export const getWalkupLineupLibraryPlayersApiPath = (companyId: number) =>
  `/v1/company/${companyId}/walkup/library-players`;

// Define dictionary type
export type IWalkupPlayersResponse = Record<string, IWalkupLibraryPlayer[]>;

// Update get function
const getWalkupLineupLibraryPlayers = (companyId: number) => {
  return apiGet(getWalkupLineupLibraryPlayersApiPath(companyId)).then(
    (response: AxiosResponse<IApiDataResponse<IWalkupPlayersResponse>>) => {
      return response.data.data;
    },
  );
};

// Hook with proper type
export const useRqWalkupLineupLibraryPlayers = (
  companyId: number,
  enabled: boolean = true,
) => {
  return useQuery<IWalkupPlayersResponse>({
    queryKey: [getWalkupLineupLibraryPlayersApiPath(companyId)],
    queryFn: () => getWalkupLineupLibraryPlayers(companyId),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: false,
    enabled,
  });
};

// Players list by student form token

export type IWalkupPreAnnouncementProp = {
  id: number;
  title: string;
};

export const getWalkupPreAnnouncementListApiPath = (
  companyId: number,
  eventId: number,
  type: string,
) => `/v1/company/${companyId}/event/${eventId}/pre-announcement/${type}`;

const getWalkupPreAnnouncementList = (
  companyId: number,
  eventId: number,
  type: string,
) => {
  return apiGet(
    getWalkupPreAnnouncementListApiPath(companyId, eventId, type),
  ).then(
    (
      response: AxiosResponse<IApiDataResponse<IWalkupPreAnnouncementProp[]>>,
    ) => {
      return response.data.data;
    },
  );
};

export const useRqWalkupPreAnnouncementList = (
  companyId: number,
  eventId: number,
  type: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [getWalkupPreAnnouncementListApiPath(companyId, eventId, type)],
    queryFn: () => getWalkupPreAnnouncementList(companyId, eventId, type),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 60 * 60 * 1000, // 1h
    retry: false,
    enabled: enabled,
  });
};
